import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {ColorPath} from 'types/Theme'

import {Box} from '@vio/atlas/helpers/Box'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import {getCssColorByPath} from '@vio/atlas/src/utils/colors'
import {Text} from '@vio/atlas/Text'
import {formatAbbreviatedPriceValue} from '@vio/core/src/price/business/formatPrice'

import {monthlyPriceTrendMessages} from './messages'
import {PricePin} from './PricePin'
import {
  BAR_HEIGHTS,
  findExtremes,
  findNearestColumn,
  getBarHeight,
  getMonthDate,
  toMonthlyRates
} from './utils'

import type {HotelAverageRateResponse} from 'seo/getHotelAverageRate'

// Box has no per-corner radius, so the flat-bottomed bar stays hand-rolled.
const Bar = styled.div<{barHeight: number; colorPath: ColorPath}>`
  width: 100%;
  height: ${({barHeight}) => barHeight}px;
  background: ${({colorPath}) => getCssColorByPath(colorPath)};
  border-radius: ${cssTheme.layout.radius.xs} ${cssTheme.layout.radius.xs} 0 0;
`

const PreventSelectionText = styled(Text)`
  user-select: none;
`

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`

const LEGEND_DOT_SIZE = 8

const BAR_COLORS = {
  mostExpensive: 'accent.c600',
  cheapest: 'success.c700',
  highlighted: 'background.brand.primary',
  default: 'neutral.c100'
} as const satisfies Record<string, ColorPath>

const LegendEntry = ({
  colorPath,
  children
}: {
  colorPath: ColorPath
  children: React.ReactNode
}) => (
  <Box display="flex" alignItems="center" gap="s250">
    <Box
      width={`${LEGEND_DOT_SIZE}px`}
      height={`${LEGEND_DOT_SIZE}px`}
      backgroundColor={colorPath}
      borderRadius="rounded"
    />
    <Text variant="bodyS">{children}</Text>
  </Box>
)

export interface MonthlyPriceTrendProps {
  /** Average nightly rates per calendar month, as returned by the average-rate endpoint. */
  averageRate: HotelAverageRateResponse
  /** Names the property in the text crawlers read, so the prices have an owner. */
  hotelName: string
  /** Month pinned while nothing is hovered, 1-12. Defaults to the current month. */
  defaultHighlightedMonth?: number
  /** Plot base rate plus taxes and fees instead of the base rate alone. */
  showTotalPrices?: boolean
}

export const MonthlyPriceTrend = ({
  averageRate: {currency, monthlyAverage},
  hotelName,
  defaultHighlightedMonth = new Date().getMonth() + 1,
  showTotalPrices = false
}: MonthlyPriceTrendProps) => {
  const {formatDate, formatMessage, locale} = useIntl()
  const {isMobile} = useDeviceLayout()
  const [highlightedMonth, setHighlightedMonth] = useState(
    defaultHighlightedMonth
  )

  const rates = useMemo(
    () => toMonthlyRates(monthlyAverage, showTotalPrices),
    [monthlyAverage, showTotalPrices]
  )

  const formatPrice = (value: number) =>
    formatAbbreviatedPriceValue({
      locale,
      value,
      currency,
      roundingStrategy: 'round',
      maximumFractionDigits: 0,
      useShortCurrency: false
    })

  // A single bar carries no trend, and the legend would name the same month twice.
  if (rates.length < 2) return null

  const extremes = findExtremes(rates)
  const {min, max, cheapestMonth, mostExpensiveMonth} = extremes
  const barHeights = isMobile ? BAR_HEIGHTS.mobile : BAR_HEIGHTS.desktop

  const getBarColor = (month: number) => {
    if (month === highlightedMonth) return BAR_COLORS.highlighted
    if (month === mostExpensiveMonth) return BAR_COLORS.mostExpensive
    if (month === cheapestMonth) return BAR_COLORS.cheapest
    return BAR_COLORS.default
  }

  const formatMonth = (month: number, style: 'narrow' | 'short' | 'long') =>
    formatDate(getMonthDate(month), {month: style})

  const highlighted = rates.find(({month}) => month === highlightedMonth)

  // Scrubbing: dragging a finger across the chart highlights each month it
  // passes, so reading the year takes one gesture instead of twelve taps.
  const highlightTouchedMonth = ({
    currentTarget,
    touches
  }: React.TouchEvent<HTMLDivElement>) => {
    const centers = Array.from(currentTarget.children).map(column => {
      const {left, width} = column.getBoundingClientRect()
      return left + width / 2
    })

    setHighlightedMonth(
      rates[findNearestColumn(centers, touches[0].clientX)].month
    )
  }

  return (
    <Box
      data-id="monthlyPriceTrend"
      role="region"
      aria-label={formatMessage(monthlyPriceTrendMessages.title)}
      display="flex"
      flexDirection="column"
      gap={isMobile ? 's500' : 's600'}
      width="100%"
    >
      <Box display="flex" flexDirection="column" gap="s500">
        <Box paddingTop={isMobile ? undefined : 's200'}>
          <Text tag="h3" variant={isMobile ? 'titleS' : 'titleM'}>
            {formatMessage(monthlyPriceTrendMessages.title)}
          </Text>
        </Box>

        <VisuallyHidden>
          <p data-id="monthlyPriceTrendSummary">
            {formatMessage(monthlyPriceTrendMessages.summary, {
              hotelName,
              cheapestMonth: formatMonth(cheapestMonth, 'long'),
              cheapestPrice: formatPrice(min),
              mostExpensiveMonth: formatMonth(mostExpensiveMonth, 'long'),
              mostExpensivePrice: formatPrice(max)
            })}
          </p>
          <table>
            <caption>
              {formatMessage(monthlyPriceTrendMessages.tableCaption, {
                hotelName,
                currency
              })}
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  {formatMessage(monthlyPriceTrendMessages.monthColumnHeader)}
                </th>
                <th scope="col">
                  {formatMessage(
                    monthlyPriceTrendMessages.averagePriceColumnHeader
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {rates.map(({month, rate}) => (
                <tr key={month}>
                  <th scope="row">{formatMonth(month, 'long')}</th>
                  <td>{formatPrice(rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </VisuallyHidden>

        {/* Padding leaves room for the price pin above the tallest bar.
            `position` plus `zIndex` isolates the pinned column's stacking
            order from the rest of the page. */}
        <Box
          aria-hidden="true"
          onMouseLeave={() => setHighlightedMonth(defaultHighlightedMonth)}
          onTouchStart={highlightTouchedMonth}
          onTouchMove={highlightTouchedMonth}
          // `pan-y` keeps the page scrollable while horizontal drags stay ours.
          style={{touchAction: 'pan-y'}}
          display="flex"
          alignItems="stretch"
          gap="s200"
          width="100%"
          paddingTop="s400"
          position="relative"
          zIndex="0"
        >
          {rates.map(({month, rate}) => (
            <Box
              key={month}
              data-id={`monthlyPriceTrendMonth-${month}`}
              onMouseEnter={() => setHighlightedMonth(month)}
              display="flex"
              flex="1 0 0"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
              gap="s200"
              minWidth="0"
              position="relative"
              zIndex={month === highlightedMonth ? '1' : '0'}
            >
              {/* The column stretches to the full chart height so the hit area
                  reaches above short bars, and this wrapper keeps the pin
                  anchored to the top of the bar itself. */}
              <Box position="relative" width="100%">
                {month === highlightedMonth && highlighted && (
                  <PricePin>
                    {formatMessage(monthlyPriceTrendMessages.pin, {
                      month: formatMonth(month, 'short'),
                      price: formatPrice(highlighted.rate)
                    })}
                  </PricePin>
                )}
                <Bar
                  data-id={`monthlyPriceTrendBar-${month}`}
                  barHeight={getBarHeight(rate, extremes, barHeights)}
                  colorPath={getBarColor(month)}
                />
              </Box>
              <PreventSelectionText
                variant="labelXS"
                colorPath="content.neutral.disabled"
              >
                {formatMonth(month, 'narrow')}
              </PreventSelectionText>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        flexWrap="wrap"
        justifyContent={isMobile ? 'flex-start' : 'center'}
        rowGap="s100"
        columnGap="s600"
      >
        <LegendEntry colorPath={BAR_COLORS.mostExpensive}>
          {formatMessage(monthlyPriceTrendMessages.mostExpensive, {
            month: formatMonth(mostExpensiveMonth, 'long'),
            price: formatPrice(max)
          })}
        </LegendEntry>
        <LegendEntry colorPath={BAR_COLORS.cheapest}>
          {formatMessage(monthlyPriceTrendMessages.cheapest, {
            month: formatMonth(cheapestMonth, 'long'),
            price: formatPrice(min)
          })}
        </LegendEntry>
      </Box>
    </Box>
  )
}
